import React, { useState } from "react";
import { Button, Dropdown, LoadingErrorState } from "../../../components";
import { useApi, useAuth, useTranslations } from "../../../contexts";
import { client } from "../../../helpers";
import { routerPaths } from "../../routerPaths";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AsyncStatus } from "../../../helpers/asyncStatus";

export const GeneralTab: React.FC = () => {
  const i18n = useTranslations();
  const navigate = useNavigate();
  const api = useApi();
  const { siteId } =
    useParams<Parameters<typeof routerPaths.siteManage>["0"]>();
  if (!siteId) throw new Error(`no site id`);
  const auth = useAuth();
  const site = api.siteById({ siteId })?.data;
  const [language, setLanguage] = useState(site?.language || "en");
  const [saveStatus, setSaveStatus] = useState<AsyncStatus>("idle");
  const [deleteStatus, setDeleteStatus] = useState<AsyncStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const saveLanguage = async (lang: string) => {
    if (saveStatus === "loading") return;
    setSaveStatus("loading");
    try {
      const results = await (
        await client.api.sites[":siteId"].$patch({
          header: auth.header,
          param: { siteId },
          json: { language: lang },
        })
      ).json();
      if (results.success) {
        setSaveStatus("success");
        toast.success(i18n["Site updated"]);
        api.queryClient.invalidateQueries({ queryKey: ["site", siteId] });
      } else {
        setSaveStatus("error");
        toast.error(i18n["Something went wrong"]);
      }
    } catch {
      setSaveStatus("error");
      toast.error(i18n["Something went wrong"]);
    }
  };

  const isDeleteConfirmed = () =>
    window.prompt(
      i18n["Are you sure you want to delete this site? To confirm, type DELETE."]
    ) === i18n.confirmDeleteWord;

  const deleteSite = async () => {
    if (deleteStatus === "loading") return;
    setDeleteStatus("loading");
    setError(null);
    try {
      const results = await (
        await client.api.sites[":siteId"].$delete({
          header: { authorization: `Bearer ${auth.token}` },
          param: { siteId },
        })
      ).json();
      if (!results.success) {
        setDeleteStatus("error");
        setError(i18n["Something went wrong"]);
        toast.error(i18n["Something went wrong"]);
        console.error(results.error.issues[0].message);
      } else {
        setDeleteStatus("success");
        api.queryClient.setQueryData<Array<{ id: string }>>(
          ["sites"],
          (sites) => sites?.filter((site) => site.id !== siteId)
        );
        api.queryClient.removeQueries({ queryKey: ["site", siteId] });
        await api.queryClient.invalidateQueries({ queryKey: ["sites"] });
        navigate(routerPaths.dashboard);
      }
    } catch (error) {
      console.error(error);
      setDeleteStatus("error");
      setError(i18n["Something went wrong"]);
      toast.error(i18n["Something went wrong"]);
    }
  };

  return (
    <div>
      <div className="mt-8">
        <div className="max-w-xs">
          <Dropdown
            options={[
              { label: `🇬🇧 ${i18n.English}`, value: "en" },
              { label: `🇫🇷 ${i18n.French}`, value: "fr" },
            ]}
            placeholder={i18n.language}
            defaultValue={language}
            onChange={(event) => {
              setLanguage(event.target.value);
              saveLanguage(event.target.value);
            }}
          />
        </div>
      </div>
      <div className="mt-8 p-3 border rounded border-red-600" aria-busy={deleteStatus === "loading"}>
        <h2 className="text-xl text-red-600 font-bold mb-4">
          {i18n["dangerZone"]}
        </h2>
        <Button
          variant="destructive"
          loading={deleteStatus === "loading"}
          onClick={(event) => {
            if (deleteStatus === "loading") {
              event.preventDefault();
              return;
            }
            if (isDeleteConfirmed()) {
              deleteSite();
            } else {
              event.preventDefault();
            }
          }}
        >
          {i18n["delete"]}
        </Button>
        <p className="mt-2 text-gray-600">
          {i18n["This action cannot be reverted or undone"]}
        </p>
        {error && (
          <LoadingErrorState
            className="mt-3"
            message={error}
            retryLabel={i18n["Retry"]}
            onRetry={() => {
              if (isDeleteConfirmed()) {
                deleteSite();
              }
            }}
          />
        )}
      </div>
    </div>
  );
};
